import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { getImageUrl } from '../../utils/imageUrl';

const CategoryList = () => {
  const MySwal = withReactContent(Swal);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Menu'); // 'Menu' or 'Inventory'

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    image: '',
    type: 'Menu',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCategories();
  }, [activeTab]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`https://avastar-pos-backend.onrender.com/api/categories?type=${activeTab}`);
      setCategories(res.data);
    } catch (err) {
      console.error(err);
      MySwal.fire('Error', 'Failed to fetch categories', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const openAddModal = () => {
    setEditingCategory(null);
    setFormData({ name: '', image: '', type: activeTab });
    setSelectedFile(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const openEditModal = (cat) => {
    setEditingCategory(cat._id);
    setFormData({
      name: cat.name,
      image: cat.image || '',
      type: cat.type,
    });
    setSelectedFile(null);
    setImagePreview(getImageUrl(cat.image));
    setShowModal(true);
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      await axios.put(`https://avastar-pos-backend.onrender.com/api/categories/${id}`, { isActive: !currentStatus });
      fetchCategories();
    } catch(err) {
      MySwal.fire('Error', 'Status change failed', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('type', formData.type);
      if (selectedFile) data.append('image', selectedFile);

      const config = { headers: { 'Content-Type': 'multipart/form-data' } };

      if (editingCategory) {
        await axios.put(`https://avastar-pos-backend.onrender.com/api/categories/${editingCategory}`, data, config);
        MySwal.fire('Success', 'Category updated', 'success');
      } else {
        await axios.post('https://avastar-pos-backend.onrender.com/api/categories', data, config);
        MySwal.fire('Success', 'Category created', 'success');
      }
      setShowModal(false);
      fetchCategories();
    } catch (err) {
      console.error(err);
      MySwal.fire('Error', err.response?.data?.message || 'Failed to save', 'error');
    }
  };

  const deleteCategory = async (id) => {
    MySwal.fire({
      title: 'Are you sure?',
      text: "This category will be permanently deleted!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`https://avastar-pos-backend.onrender.com/api/categories/${id}`);
          MySwal.fire('Deleted!', 'Category has been deleted.', 'success');
          fetchCategories();
        } catch (err) {
          console.error(err);
          MySwal.fire('Error', 'Failed to delete category', 'error');
        }
      }
    });
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header d-flex justify-content-between align-items-center mb-4">
          <div className="page-title">
            <h4>Categories Management</h4>
            <h6>Manage Menu and Inventory Categories</h6>
          </div>
          <div className="page-btn">
            <button className="btn btn-added" onClick={openAddModal}>
              <i className="fas fa-plus me-2"></i> Add {activeTab} Category
            </button>
          </div>
        </div>

        {/* Custom Tabs */}
        <div className="card mb-4 bg-white shadow-sm border-0">
          <div className="card-body p-0">
            <ul className="nav nav-pills p-3 bg-light rounded" style={{ gap: '10px' }}>
              <li className="nav-item">
                <button
                  className={`nav-link fw-bold px-4 py-2 rounded-pill ${activeTab === 'Menu' ? 'active shadow-sm bg-primary' : 'text-secondary'}`}
                  onClick={() => setActiveTab('Menu')}
                  style={{ transition: 'all 0.3s ease' }}
                >
                  <i className="fas fa-utensils me-2"></i>Menu Categories
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link fw-bold px-4 py-2 rounded-pill ${activeTab === 'Restaurant Inventory' ? 'active shadow-sm bg-primary' : 'text-secondary'}`}
                  onClick={() => setActiveTab('Restaurant Inventory')}
                  style={{ transition: 'all 0.3s ease' }}
                >
                  <i className="fas fa-boxes me-2"></i>Restaurant Inventory
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link fw-bold px-4 py-2 rounded-pill ${activeTab === 'Hotel Inventory' ? 'active shadow-sm bg-primary' : 'text-secondary'}`}
                  onClick={() => setActiveTab('Hotel Inventory')}
                  style={{ transition: 'all 0.3s ease' }}
                >
                  <i className="fas fa-bed me-2"></i>Hotel Inventory
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Categories Table */}
        <div className="card shadow-sm border-0">
          <div className="card-body">
            {loading ? (
              <div className="text-center p-5">Loading categories...</div>
            ) : categories.length === 0 ? (
              <div className="text-center p-5 text-muted">No {activeTab} categories found.</div>
            ) : (
              <div className="table-responsive">
                <table className="table border-bottom table-hover">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '60px' }}>Image</th>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((cat) => (
                      <tr key={cat._id} className="align-middle">
                        <td>
                          {cat.image ? (
                            <img src={getImageUrl(cat.image)} alt={cat.name} style={{ width: '44px', height: '44px', borderRadius: '10px', objectFit: 'cover' }} />
                          ) : (
                            <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                              <i className="fas fa-image"></i>
                            </div>
                          )}
                        </td>
                        <td className="fw-bold">{cat.name}</td>
                        <td>
                          <span className={`badge ${cat.type === 'Menu' ? 'bg-primary' : cat.type === 'Restaurant Inventory' ? 'bg-warning' : 'bg-info'} bg-opacity-10 text-primary px-3 py-2 rounded-pill`}>
                            {cat.type}
                          </span>
                        </td>
                        <td>
                          <div className="form-check form-switch pt-1">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={cat.isActive}
                              onChange={() => toggleStatus(cat._id, cat.isActive)}
                              style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                            />
                            <label className="form-check-label ms-2">
                              {cat.isActive ? (
                                <span className="text-success fw-bold">Active</span>
                              ) : (
                                <span className="text-danger fw-bold">Disabled</span>
                              )}
                            </label>
                          </div>
                        </td>
                        <td>
                          <button className="btn btn-sm btn-outline-primary d-inline-flex align-items-center" onClick={() => openEditModal(cat)}>
                            <i className="fas fa-edit me-1"></i> Edit
                          </button>
                          <button className="btn btn-sm btn-outline-danger d-inline-flex align-items-center ms-2" onClick={() => deleteCategory(cat._id)}>
                            <i className="fas fa-trash-alt me-1"></i> Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header bg-light border-bottom-0 pb-3">
                <h5 className="modal-title fw-bold">
                  {editingCategory ? 'Edit Category' : `Add New ${activeTab} Category`}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label className="form-label fw-bold text-muted">Category Name</label>
                    <input
                      type="text"
                      className="form-control form-control-lg bg-light border-0"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g. Rice & Curry"
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label className="form-label fw-bold text-muted">Category Type</label>
                    <input
                      type="text"
                      className="form-control form-control-lg bg-light border-0"
                      value={formData.type}
                      disabled
                    />
                  </div>
                  <div className="mb-4">
                    <label className="form-label fw-bold text-muted">Category Image</label>
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      style={{ 
                        border: '2px dashed #d1d5db', borderRadius: '12px', 
                        padding: '20px', textAlign: 'center', cursor: 'pointer',
                        background: '#f9fafb', transition: 'border-color 0.2s'
                      }}
                    >
                      {(imagePreview || selectedFile) ? (
                        <img src={selectedFile ? URL.createObjectURL(selectedFile) : imagePreview} alt="preview" 
                          style={{ maxHeight: '120px', borderRadius: '8px', objectFit: 'cover' }} />
                      ) : (
                        <div>
                          <i className="fas fa-cloud-upload-alt text-primary" style={{ fontSize: '2rem', marginBottom: '8px' }}></i>
                          <p className="text-muted mb-0" style={{ fontSize: '13px' }}>Click to upload image</p>
                          <small className="text-muted">Auto-compressed to optimal size</small>
                        </div>
                      )}
                    </div>
                    <input 
                      type="file" ref={fileInputRef} accept="image/*" 
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const f = e.target.files[0];
                        if (f) { setSelectedFile(f); setImagePreview(null); }
                      }} 
                    />
                  </div>
                  <div className="text-end mt-2">
                    <button type="button" className="btn btn-light px-4 py-2 me-2 fw-bold" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary px-4 py-2 fw-bold shadow-sm">Save Category</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryList;
